import React from "react";
import ReactMarkdown from "react-markdown";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ---------- shared helpers ----------

// Phase 12.1 — severity rules: minimal-glyph (dot + text) for low/medium,
// filled chip ONLY for high-severity. Mirrors the StatusPill pattern.
const SEVERITY_STYLE = {
  low: { dot: "#F5D90A", text: "var(--page-fg-secondary)" },
  medium: { dot: "#F76808", text: "var(--page-fg)" },
  high: {
    bg: "var(--danger-bg)",
    color: "var(--danger-fg)",
    border: "var(--danger-border)",
  },
};

function SeverityBadge({ severity }) {
  if (severity === "high") {
    const s = SEVERITY_STYLE.high;
    return (
      <span
        data-testid={`severity-badge-${severity}`}
        className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
        style={{
          backgroundColor: s.bg,
          color: s.color,
          boxShadow: `inset 0 0 0 1px ${s.border}`,
        }}
      >
        {severity}
      </span>
    );
  }
  const s = SEVERITY_STYLE[severity] || SEVERITY_STYLE.low;
  return (
    <span
      data-testid={`severity-badge-${severity}`}
      className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-[0.06em]"
      style={{ color: s.text }}
    >
      <span
        aria-hidden
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: s.dot }}
      />
      {severity}
    </span>
  );
}

function Panel({
  title,
  subtitle,
  icon: Icon,
  aiGenerated = false,
  rightSlot,
  testid,
  children,
}) {
  return (
    <div data-testid={testid} className="brand-card p-6">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5">
          {Icon && (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-subtle)",
                color: aiGenerated ? "var(--warning-fg)" : "var(--page-fg)",
              }}
            >
              <Icon className="h-4 w-4" strokeWidth={1.7} />
            </div>
          )}
          <div>
            <h3
              className="text-sm font-semibold"
              style={{ color: "var(--page-fg)", letterSpacing: "-0.005em" }}
            >
              {title}
              {aiGenerated && (
                <span
                  className="ml-2 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.10em]"
                  style={{
                    backgroundColor: "var(--warning-bg)",
                    color: "var(--warning-fg)",
                    boxShadow: "inset 0 0 0 1px var(--warning-border)",
                  }}
                >
                  <Sparkles className="h-3 w-3" strokeWidth={1.7} />
                  AI
                </span>
              )}
            </h3>
            {subtitle && (
              <p
                className="mt-0.5 text-xs"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {rightSlot}
      </div>
      {children}
    </div>
  );
}

function AiDisclaimer() {
  return (
    <p
      data-testid="ai-disclaimer"
      className="mt-3 text-[10px] font-medium uppercase tracking-[0.14em]"
      style={{ color: "var(--page-fg-muted)" }}
    >
      AI insights · advisory · review by HR before action
    </p>
  );
}

function LiveCachedPill({ cached, generatedAt, llmAvailable }) {
  const label = cached ? "Cached" : "Live";
  const style =
    llmAvailable === false
      ? { bg: "var(--danger-bg)", color: "var(--danger-fg)", border: "var(--danger-border)" }
      : cached
      ? { bg: "var(--surface-2)", color: "var(--page-fg-secondary)", border: "var(--border-subtle)" }
      : { bg: "var(--success-bg)", color: "var(--success-fg)", border: "var(--success-border)" };
  const when = generatedAt ? new Date(generatedAt).toLocaleTimeString() : null;
  return (
    <span
      data-testid="live-cached-pill"
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        boxShadow: `inset 0 0 0 1px ${style.border}`,
      }}
    >
      {label}
      {when && <span className="opacity-70">· {when}</span>}
    </span>
  );
}

// ---------- Executive Narrative ----------

export function NarrativePanel({
  data,
  loading,
  canRegenerate,
  onRegenerate,
  regenerating,
}) {
  return (
    <Panel
      testid="panel-narrative"
      title="Executive narrative"
      subtitle="2–4 paragraph summary of the current filter window"
      icon={Sparkles}
      aiGenerated
      rightSlot={
        <div className="flex items-center gap-2">
          <LiveCachedPill
            cached={data?.cached}
            generatedAt={data?.generated_at}
            llmAvailable={data?.llm_available}
          />
          {canRegenerate && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRegenerate}
              disabled={regenerating}
              data-testid="narrative-regenerate-button"
              className="rounded-md border-[color:var(--warning-border)] bg-transparent hover:bg-[color:var(--warning-bg)]"
              style={{ color: "var(--warning-fg)" }}
            >
              <RefreshCw
                className={`mr-1 h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`}
              />
              Regenerate
            </Button>
          )}
        </div>
      }
    >
      {loading && (
        <div className="h-24 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
      )}
      {!loading && data?.llm_available === false && (
        <div
          data-testid="ai-unavailable-banner"
          className="mb-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-[11px]"
          style={{
            backgroundColor: "rgba(229, 72, 77, 0.06)",
            borderColor: "rgba(229, 72, 77, 0.30)",
            color: "#B42318",
          }}
        >
          <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.8} />
          <span>
            AI unavailable — showing rule-based insights only.
            {data?.llm_error ? ` (${String(data.llm_error).slice(0, 100)})` : ""}
          </span>
        </div>
      )}
      {!loading && data?.narrative_md && (
        <div
          data-testid="narrative-markdown"
          className="prose prose-sm max-w-none prose-p:my-2 prose-p:text-[13px] prose-p:leading-relaxed prose-p:text-[color:var(--page-fg-secondary)] prose-strong:text-[color:var(--page-fg)] prose-strong:font-semibold prose-headings:text-[color:var(--page-fg)] prose-em:text-[color:var(--warning-fg)] prose-li:text-[color:var(--page-fg-secondary)] prose-h1:text-base prose-h2:text-base prose-h3:text-sm"
        >
          <ReactMarkdown>{data.narrative_md}</ReactMarkdown>
        </div>
      )}
      <AiDisclaimer />
    </Panel>
  );
}

// ---------- Pattern Detection ----------

export function PatternsPanel({ data, loading, onDrill }) {
  const p = data?.patterns || {};
  return (
    <Panel
      testid="panel-patterns"
      title="Pattern detection"
      subtitle="Deterministic rules applied to the current filter window"
      icon={TrendingUp}
    >
      {loading ? (
        <div className="h-32 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <SubSection
            title="Departments with elevated case volume"
            testid="patterns-departments"
            items={p.departments}
            empty="No elevated departments."
            renderRow={(d) => (
              <>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[color:var(--page-fg)]">{d.department}</p>
                  <p className="text-[11px] text-[color:var(--page-fg-secondary)]">{d.case_count} cases</p>
                </div>
                <SeverityBadge severity={d.severity} />
              </>
            )}
          />
          <SubSection
            title="HR partners with elevated open caseload"
            testid="patterns-managers"
            items={p.managers}
            empty="No overloaded HR partners."
            onRowClick={(m) => onDrill?.(m.drill)}
            renderRow={(m) => (
              <>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[color:var(--page-fg)]">{m.name}</p>
                  <p className="text-[11px] text-[color:var(--page-fg-secondary)]">
                    {m.open_case_count} open cases
                  </p>
                </div>
                <SeverityBadge severity={m.severity} />
              </>
            )}
          />
          <SubSection
            title="Repeat employees"
            testid="patterns-repeats"
            items={p.repeat_employees}
            empty="No repeat employees in this window."
            onRowClick={(r) => onDrill?.(r.drill)}
            renderRow={(r) => (
              <>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-[color:var(--page-fg)]">{r.name}</p>
                  <p className="text-[11px] text-[color:var(--page-fg-secondary)]">
                    #{r.position_id || r.file_number} · {r.service_line} · {r.case_count} cases
                  </p>
                </div>
                <SeverityBadge severity={r.severity} />
              </>
            )}
          />
          <SubSection
            title="Issue clusters (same issue type in one department)"
            testid="patterns-clusters"
            items={p.issue_clusters}
            empty="No clusters detected."
            onRowClick={(c) => onDrill?.(c.drill)}
            renderRow={(c) => (
              <>
                <div className="flex-1 min-w-0">
                  <p className="truncate text-sm font-medium text-[color:var(--page-fg)]">
                    {c.issue_type} · {c.department}
                  </p>
                  <p className="text-[11px] text-[color:var(--page-fg-secondary)]">{c.case_count} cases</p>
                </div>
                <SeverityBadge severity={c.severity} />
              </>
            )}
          />
        </div>
      )}
    </Panel>
  );
}

function SubSection({ title, items, empty, renderRow, onRowClick, testid }) {
  const rows = items || [];
  return (
    <div data-testid={testid}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
        {title}
      </p>
      {rows.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-[color:var(--border-subtle)] px-3 py-5 text-center text-[11px] text-[color:var(--page-fg-secondary)]"
          style={{ backgroundColor: "var(--surface-2)" }}
        >
          {empty}
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => {
            const content = (
              <div
                className="flex items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2"
                style={{ backgroundColor: "var(--surface-2)" }}
              >
                {renderRow(r)}
              </div>
            );
            return onRowClick ? (
              <button
                key={r.key}
                onClick={() => onRowClick(r)}
                className="block w-full text-left rounded-lg transition-colors hover:bg-[color:var(--surface)]"
                data-testid={`pattern-row-${r.key}`}
              >
                {content}
              </button>
            ) : (
              <div key={r.key} data-testid={`pattern-row-${r.key}`}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- High-Risk Watchlist ----------

export function WatchlistPanel({ data, loading }) {
  const items = data?.items || [];
  return (
    <Panel
      testid="panel-watchlist"
      title="High-risk watchlist"
      subtitle={`Risk score 0–100 · ${items.length} open high-risk case${items.length === 1 ? "" : "s"}`}
      icon={ShieldAlert}
    >
      {loading ? (
        <div className="h-24 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-[color:var(--border-subtle)] px-3 py-6 text-center text-[11px] text-[color:var(--page-fg-secondary)]"
          style={{ backgroundColor: "var(--surface-2)" }}
        >
          No open high-risk cases in the current window.
        </div>
      ) : (
        <TooltipProvider>
          <div className="space-y-1">
            {items.map((row) => {
              const scoreStyle =
                row.score >= 60
                  ? { bg: "var(--danger-bg)", color: "var(--danger-fg)", border: "var(--danger-border)" }
                  : row.score >= 30
                  ? { bg: "rgba(247, 104, 8, 0.12)", color: "var(--page-fg)", border: "rgba(247, 104, 8, 0.40)" }
                  : { bg: "var(--surface-2)", color: "var(--page-fg-secondary)", border: "var(--border-subtle)" };
              return (
                <Link
                  key={row.id}
                  to={`/cases/${row.id}`}
                  data-testid={`watchlist-row-${row.case_id}`}
                  className="flex items-center gap-3 rounded-lg border border-[color:var(--border-subtle)] px-3 py-2 transition-colors hover:bg-[color:var(--surface)]"
                  style={{ backgroundColor: "var(--surface-2)" }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums"
                        style={{
                          backgroundColor: scoreStyle.bg,
                          color: scoreStyle.color,
                          boxShadow: `inset 0 0 0 1px ${scoreStyle.border}`,
                        }}
                      >
                        {row.score}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="mb-1 text-[11px] font-semibold">Score breakdown</p>
                      <ul className="space-y-0.5 text-[11px]">
                        {(row.breakdown || []).map((b, i) => (
                          <li key={i}>
                            · {b.factor.replace(/_/g, " ")} <b>+{b.points}</b>
                          </li>
                        ))}
                        {(!row.breakdown || row.breakdown.length === 0) && (
                          <li className="opacity-70">No contributing factors</li>
                        )}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs tabular-nums" style={{ color: "var(--page-fg)" }}>{row.case_id}</span>
                      <span className="truncate text-sm text-[color:var(--page-fg)]">
                        {row.team_member_name} · {row.service_line}
                      </span>
                    </div>
                    <p className="truncate text-[11px] text-[color:var(--page-fg-secondary)]">
                      {row.issue_type} — {row.specific_issue} · {row.status}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </TooltipProvider>
      )}
    </Panel>
  );
}

// ---------- Anomaly Spotlight ----------

export function AnomaliesPanel({ data, loading }) {
  const spikes = data?.anomalies || { service_lines: [], issue_types: [] };
  const any = spikes.service_lines.length + spikes.issue_types.length > 0;
  return (
    <Panel
      testid="panel-anomalies"
      title="Anomaly spotlight"
      subtitle={
        data
          ? `current: ${data.current_total} · prior: ${data.prior_total}`
          : "Month-over-month spikes"
      }
      icon={AlertTriangle}
    >
      {loading ? (
        <div className="h-16 animate-pulse rounded-xl bg-[color:var(--surface-2)]" />
      ) : !any ? (
        <div
          className="rounded-xl border border-dashed border-[color:var(--border-subtle)] px-3 py-5 text-center text-[11px] text-[color:var(--page-fg-secondary)]"
          style={{ backgroundColor: "var(--surface-2)" }}
        >
          No significant spikes in the current window.
        </div>
      ) : (
        <div className="space-y-3">
          {spikes.service_lines.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
                Service line spikes
              </p>
              <div className="space-y-1">
                {spikes.service_lines.map((s, i) => (
                  <SpikeRow key={`sl-${s.key}-${i}`} item={s} />
                ))}
              </div>
            </div>
          )}
          {spikes.issue_types.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--page-fg-secondary)]">
                Issue type spikes
              </p>
              <div className="space-y-1">
                {spikes.issue_types.map((s, i) => (
                  <SpikeRow key={`it-${s.key}-${i}`} item={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function SpikeRow({ item }) {
  const pct = item.pct_change;
  return (
    <div
      data-testid={`anomaly-row-${item.key}`}
      className="flex items-center gap-3 rounded-xl border border-[color:var(--border-subtle)] px-3 py-2"
      style={{ backgroundColor: "var(--surface-2)" }}
    >
      <TrendingUp className="h-4 w-4" strokeWidth={1.8} style={{ color: "var(--page-fg-muted)" }} />
      <div className="flex-1">
        <p className="text-sm font-medium text-[color:var(--page-fg)]">{item.key}</p>
        <p className="text-[11px] text-[color:var(--page-fg-secondary)]">
          {item.previous} → {item.current}
          {pct != null ? ` (+${pct}%)` : " (new)"}
        </p>
      </div>
    </div>
  );
}

// ---------- AI Summary strip for /dashboard ----------

export function AiSummaryStrip({ data, loading, filtersQuery }) {
  const navigate = useNavigate();
  const firstPara = (data?.narrative_md || "").split(/\n\n+/)[0] || "";
  return (
    <div
      data-testid="ai-summary-strip"
      className={`mb-6 flex items-start gap-3 rounded-xl px-4 py-3 ${
        loading ? "animate-pulse" : ""
      }`}
      style={{
        backgroundColor: "var(--surface)",
        border: "1px solid var(--border-subtle)",
        boxShadow: "var(--inset-highlight)",
      }}
      style={{
        background:
          "linear-gradient(90deg, rgba(214, 177, 106, 0.12) 0%, rgba(20, 184, 184, 0.06) 60%, transparent 100%)",
      }}
    >
      <div
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg"
        style={{
          backgroundColor: "var(--warning-bg)",
          color: "var(--warning-fg)",
          boxShadow: "inset 0 0 0 1px var(--warning-border)",
        }}
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.7} />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--warning-fg)" }}
        >
          AI Summary
        </p>
        {loading ? (
          <p className="text-xs text-[color:var(--page-fg-secondary)]">Generating…</p>
        ) : data?.llm_available === false ? (
          <p className="text-xs text-[color:var(--page-fg-secondary)]">
            AI unavailable. Viewing rule-based summary →
          </p>
        ) : (
          <div className="text-sm text-[color:var(--page-fg-secondary)] line-clamp-2">
            <ReactMarkdown
              components={{ p: ({ children }) => <span>{children}</span> }}
            >
              {firstPara.slice(0, 260)}
            </ReactMarkdown>
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate(`/insights${filtersQuery ? "?" + filtersQuery : ""}`)}
        data-testid="ai-summary-view-insights-button"
        className="flex-shrink-0 rounded-md border-[color:var(--warning-border)] bg-transparent hover:bg-[color:var(--warning-bg)]"
        style={{ color: "var(--warning-fg)" }}
      >
        View full insights →
      </Button>
    </div>
  );
}
